import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { 
  Home, ArrowLeftRight, History, CreditCard, User, 
  FileText, Receipt, LogOut, Shield
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";

const navItems = [
  { path: "/dashboard", icon: Home, label: "Dashboard" },
  { path: "/transfer", icon: ArrowLeftRight, label: "Transfer" },
  { path: "/transactions", icon: History, label: "Transactions" },
  { path: "/cards", icon: CreditCard, label: "Cards" },
  { path: "/loans", icon: FileText, label: "Loans" },
  { path: "/bills", icon: Receipt, label: "Bill Payment" },
  { path: "/profile", icon: User, label: "Profile" },
];

const adminItems = [
  { path: "/admin", icon: Shield, label: "Admin Panel" },
];

export const Sidebar = () => {
  const location = useLocation();
  const { logout, isAdmin } = useAuth();

  const NavItem = ({ item, isPartialMatch = false }) => {
    const isActive = isPartialMatch 
      ? location.pathname.startsWith(item.path)
      : location.pathname === item.path || (item.path === "/transfer" && location.pathname.includes("/transfer"));

    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={cn(
          "group relative flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 mx-2",
          isActive
            ? "bg-primary/10 text-primary shadow-[0_0_20px_rgba(var(--primary),0.05)]"
            : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
        )}
      >
        {/* Active Indicator Bar */}
        {isActive && (
          <div className="absolute left-0 w-1 h-5 bg-primary rounded-r-full" />
        )}
        
        <item.icon className={cn(
          "w-5 h-5 transition-transform duration-300 group-hover:scale-110",
          isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600"
        )} />
        
        <span className="font-semibold text-[13px] tracking-tight">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="hidden md:flex flex-col w-64 h-screen fixed left-0 top-0 border-r border-slate-100 bg-white/80 backdrop-blur-xl z-40">
      {/* Brand Header */}
      <div className="p-6 mb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-[15px] tracking-tight text-slate-900 leading-none">HaysPrime</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary mt-1 opacity-80">Enterprise</p>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto pt-2">
        {navItems.map((item) => (
          <NavItem key={item.path} item={item} />
        ))}

        {isAdmin && (
          <div className="mt-8 px-4">
            <div className="px-4 py-2 flex items-center gap-2">
              <div className="h-[1px] flex-1 bg-slate-100" />
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Management</p>
              <div className="h-[1px] flex-1 bg-slate-100" />
            </div>
            <div className="mt-2 space-y-1">
              {adminItems.map((item) => (
                <NavItem key={item.path} item={item} isPartialMatch />
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Logout Section */}
      <div className="p-4 mt-auto">
        <button
          onClick={logout}
          className="group flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all duration-300 border border-transparent hover:border-red-100"
        >
          <LogOut className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
          <span className="font-bold text-[13px] tracking-tight">Sign Out</span>
        </button>
      </div>
    </aside>
  );
};